import { Component, ErrorInfo, ReactNode } from 'react';
import { trackException } from '../../services/appInsights';

interface Props {
  children: ReactNode;
  projectId: string;
  onRetry?: () => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export default class GameErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Game Error:', error, errorInfo);
    trackException(error, {
      componentStack: errorInfo.componentStack || '',
      errorBoundary: 'GameErrorBoundary',
      projectId: this.props.projectId,
      url: window.location.href,
    });
    this.reportError(error, errorInfo);
  }

  async reportError(error: Error, errorInfo: ErrorInfo) {
    try {
      await fetch(`/api/games/${this.props.projectId}/analytics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventType: 'error',
          errorType: error.name,
          errorMessage: `${error.message}\n\nComponent Stack:\n${errorInfo.componentStack}`,
        }),
      });
    } catch (reportError) {
      console.error('Failed to report error:', reportError);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
    if (this.props.onRetry) this.props.onRetry();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'var(--bg)',
            color: 'var(--text)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 5,
            padding: 24,
          }}
        >
          <div style={{ maxWidth: 440, textAlign: 'center' }}>
            <div className="eyebrow" style={{ color: 'var(--danger)', marginBottom: 10 }}>Error</div>
            <h2 className="display tight" style={{ fontSize: 26, margin: '0 0 12px' }}>Game error</h2>
            <p className="muted-2" style={{ fontSize: 13.5, lineHeight: 1.55, marginBottom: 18 }}>
              Something went wrong while loading the game. This could be a browser-compatibility or network issue.
            </p>

            {this.state.error && (
              <details style={{ marginBottom: 22, textAlign: 'left' }}>
                <summary className="muted" style={{ cursor: 'pointer' }}>Technical details</summary>
                <div
                  className="mono"
                  style={{
                    marginTop: 8,
                    padding: '10px 12px',
                    background: 'var(--bg-2)',
                    border: '1px solid var(--line)',
                    borderRadius: 8,
                    fontSize: 12,
                    color: 'var(--danger)',
                  }}
                >
                  {this.state.error.message}
                </div>
              </details>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button type="button" onClick={this.handleRetry} className="btn primary lg" style={{ justifyContent: 'center' }}>
                Try again
              </button>
              <button type="button" onClick={() => window.location.reload()} className="btn lg" style={{ justifyContent: 'center' }}>
                Reload page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
